import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { Row, Col, Space, Typography } from 'antd';
import PnL from '../../components/PnL';
import Article from '../../components/Article';

const normalizePrices = (prices) => {
  const startPrice = prices[0];
  return prices.map(price => (price / startPrice) * 100);
};

const calculateDrawdowns = (prices) => {
  let runningMax = -Infinity;
  let maxDrawdown = 0;
  const drawdown = [];

  for (let price of prices) {
    runningMax = Math.max(runningMax, price);
    const currentDrawdown = ((price - runningMax) / runningMax) * 100;
    drawdown.push(currentDrawdown);
    maxDrawdown = Math.min(maxDrawdown, currentDrawdown);
  }

  return {
    drawdown,
    maxDrawdown,
    totalReturn: ((prices[prices.length - 1] / prices[0]) - 1) * 100
  };
};

const DEFAULT_COLORS = [
  '#008bff',  // blue
  '#ff4d4d',  // red
  '#00cc66',  // green
  '#ff9933',  // orange
  '#9933ff',  // purple
  '#ff66b2',  // pink
];

const SINGLE_SERIES_DRAWDOWN_COLOR = '#ff4d4d';

const hexToRgba = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const findMaxDrawdownPeriod = (drawdown) => {
  let maxDrawdownStart = 0;
  let maxDrawdownEnd = 0;
  let minDrawdown = 0;
  let recoveryPoint = drawdown.length - 1;

  // Find the lowest point (max drawdown)
  for (let i = 0; i < drawdown.length; i++) {
    if (drawdown[i] < minDrawdown) {
      minDrawdown = drawdown[i];
      maxDrawdownEnd = i;
    }
  }

  // Find start of drawdown period (last peak before max drawdown)
  for (let i = maxDrawdownEnd; i >= 0; i--) {
    if (drawdown[i] === 0) {
      maxDrawdownStart = i;
      break;
    }
  }

  // Find recovery point
  for (let i = maxDrawdownEnd; i < drawdown.length; i++) {
    if (drawdown[i] >= 0) {
      recoveryPoint = i;
      break;
    }
  }

  return { maxDrawdownStart, maxDrawdownEnd, recoveryPoint };
};


const createAnnotations = (seriesData, dates) => {
  if (seriesData.length !== 1) return [];

  const { maxDrawdownStart, maxDrawdownEnd, recoveryPoint } = findMaxDrawdownPeriod(seriesData[0].drawdown);

  return [
    {
      x: dates[maxDrawdownStart],
      y: 0.95,
      yref: 'paper',
      text: 'Goes Underwater',
      showarrow: true,
      arrowhead: 2,
      arrowsize: 1,
      arrowwidth: 1,
      ax: 0,
      ay: -40,
      arrowcolor: '#ccc',
      font: {
        color: 'rgba(0,0,0,0.6)',
        size: 12
      },
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      borderwidth: 2
    },
    {
      x: dates[maxDrawdownEnd],
      y: 0.95,
      yref: 'paper',
      text: 'Max DD',
      showarrow: true,
      arrowhead: 2,
      arrowsize: 1,
      arrowwidth: 1,
      ax: 0,
      ay: -40,
      arrowcolor: '#ccc',
      font: {
        color: 'red',
        size: 12
      },
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      borderwidth: 1
    },
    {
      x: dates[recoveryPoint],
      y: 0.95,
      yref: 'paper',
      text: 'Recovery Point',
      showarrow: true,
      arrowhead: 2,
      arrowsize: 1,
      arrowwidth: 1,
      ax: 0,
      ay: -40,
      arrowcolor: '#ccc',
      font: {
        color: '#00cc66',
        size: 12
      },
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      borderwidth: 1
    }
  ];
};


const UnderwaterPlot = ({
  series = undefined,
  prices = [],
  dates = [],
  title = "",
  drawdownThreshold = 0,
  height = 800,
  colors = {
    price: '#008bff',
    threshold: '#d6d6d6',
    grid: '#e6e6e6',
    background: 'white'
  }
}) => {
  const normalizedSeries = useMemo(() => {
    if (series) {
      return series.map((s, index) => ({
        ...s,
        prices: normalizePrices(s.prices),
        originalPrices: s.prices,
        color: s.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]
      }));
    }
    if (prices.length > 0) {
      return [{
        prices: normalizePrices(prices),
        originalPrices: prices,
        dates,
        title,
        color: colors.price
      }];
    }
    return [];
  }, [series, prices, dates, title, colors]);

  const seriesData = useMemo(() => {
    return normalizedSeries.map(series => ({
      ...series,
      ...calculateDrawdowns(series.prices)
    }));
  }, [normalizedSeries]);

const plotData = useMemo(() => {
    const data = [];
    const isMultipleSeries = seriesData.length > 1;

    seriesData.forEach((series, seriesIndex) => {
      const { prices, dates, drawdown, title, color } = series;
      const { maxDrawdownStart, maxDrawdownEnd, recoveryPoint } = findMaxDrawdownPeriod(drawdown);

      // Add price line
      data.push({
        x: dates,
        y: prices,
        name: title || `Series ${seriesIndex + 1}`,
        type: 'scatter',
        line: {
          color: color,
          width: 2
        },
        xaxis: 'x',
        yaxis: 'y1',
        hovertemplate: '%{y:.1f}%<extra></extra>'
      });

      // Add vertical annotations for drawdown period
      if (!isMultipleSeries) {
        data.push({
          x: [dates[maxDrawdownStart], dates[maxDrawdownStart]],
          y: [Math.min(...prices), Math.max(...prices)],
          mode: 'lines',
          name: 'Start',
          line: {
            color: 'rgba(0,0,0,0.4)',
            width: 1,
            dash: 'dot'
          },
          xaxis: 'x',
          yaxis: 'y1',
          showlegend: false
        });
        data.push({
          x: [dates[maxDrawdownEnd], dates[maxDrawdownEnd]],
          y: [Math.min(...prices), Math.max(...prices)],
          mode: 'lines',
          name: 'Max DD',
          line: {
            color: 'red',
            width: 2,
            dash: 'dash'
          },
          xaxis: 'x',
          yaxis: 'y1',
          showlegend: false
        });
        data.push({
          x: [dates[recoveryPoint], dates[recoveryPoint]],
          y: [Math.min(...prices), Math.max(...prices)],
          mode: 'lines',
          name: 'Recovery',
          line: {
            color: 'green',
            width: 2,
            dash: 'dot'
          },
          xaxis: 'x',
          yaxis: 'y1',
          showlegend: false
        });
      }

      // Create array with only max drawdown period visible
      const maxDrawdownArray = drawdown.map((value, index) => {
        if (index >= maxDrawdownStart && index <= recoveryPoint) {
          return value;
        }
        return null;
      });

      // Add drawdown visualization
      data.push({
        x: dates,
        y: maxDrawdownArray,
        name: `${title || `Series ${seriesIndex + 1}`} Max Drawdown`,
        type: 'scatter',
        fill: isMultipleSeries ? 'none' : 'tozeroy',
        line: {
          color: isMultipleSeries ? color : 'transparent',
          width: isMultipleSeries ? 2 : 0
        },
        mode: isMultipleSeries ? 'lines' : 'none',
        fillcolor: isMultipleSeries ? 'none' : hexToRgba(SINGLE_SERIES_DRAWDOWN_COLOR, 0.7),
        xaxis: 'x',
        yaxis: 'y2',
        showlegend: false,
        hovertemplate: '%{y:.1f}%<extra></extra>'
      });
    });

    if (drawdownThreshold) {
      data.push({
        x: [normalizedSeries[0]?.dates[0], normalizedSeries[0]?.dates[normalizedSeries[0]?.dates.length - 1]],
        y: [drawdownThreshold, drawdownThreshold],
        name: `Threshold (${drawdownThreshold}%)`,
        line: {
          color: colors.threshold,
          dash: 'dash',
          width: 1,
        },
        xaxis: 'x',
        yaxis: 'y2',
      });
    }

    return data;
  }, [seriesData, colors, drawdownThreshold]);

  const layout = {
    grid: {
      rows: 2,
      columns: 1,
      pattern: 'independent',
    },
    height,
    showlegend: true,
    legend: {
      orientation: 'h',
      x: 1,
      xanchor: 'right',
      y: 1
    },
    plot_bgcolor: colors.background,
    paper_bgcolor: colors.background,
    margin: {
      b: 50,
      t: 50,
      l: 50,
      r: 50,
      pad: 0,
    },
    xaxis: {
      gridcolor: '#e0e0e0',
      rangeslider: { visible: true },
      showgrid: false
    },
    yaxis: {
      gridcolor: '#eeefff',
      domain: [0.35, 1], // Adjusted to make top plot larger
      zerolinecolor: colors.grid,
      tickformat: '.0f',
      ticksuffix: '%',
    },
    yaxis2: {
      gridcolor: '#e0e0e0',
      domain: [0, 0.25], // Adjusted to make bottom plot smaller
      zerolinecolor: colors.grid,
      tickformat: '.0f',
      ticksuffix: '%'
    },
    annotations: seriesData.length === 1 ? createAnnotations(seriesData, seriesData[0].dates) : []
  };


  return (
    <div className="w-full h-4/5">
      <Row align="middle" gutter={16} justify="start" className="px-8">
        <Col>
          <Typography.Text>{title?.toUpperCase()}</Typography.Text>
        </Col>
        <Col>
          <Space className="px-8">
            {seriesData.map((series, index) => (
              <Space key={index} className="mx-3">
                <Typography.Text style={{ color: series.color }}>
                  {series.title || `Series ${index + 1}`}
                </Typography.Text>
                <Space>
                  <Typography.Text>Max DD</Typography.Text>
                  <PnL value={series.maxDrawdown} multiplier={1} />
                </Space>
                <Space>
                  <Typography.Text>Return</Typography.Text>
                  <PnL value={series.totalReturn} multiplier={1} />
                </Space>
              </Space>
            ))}
          </Space>
        </Col>
      </Row>
      <Plot
        data={plotData}
        layout={layout}
        config={{
          responsive: true,
          displayModeBar: false,
          displaylogo: false,
        }}
        className="w-full"
      />
    </div>
  );
};

export default UnderwaterPlot;